import { definePreset } from '@primeuix/themes';
import type { Preset } from '@primeuix/themes/types';
import type { ColorScale } from '@primeuix/styled';

type Rgb = { r: number; g: number; b: number };

const hexToRgb = (hex: string): Rgb => {
  const normalized = hex.replace('#', '').toLowerCase();
  const value =
    normalized.length === 3
      ? normalized
          .split('')
          .map((ch) => ch + ch)
          .join('')
      : normalized;
  const num = parseInt(value, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
};

const rgbToHex = ({ r, g, b }: Rgb): string => {
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const mix = (color: Rgb, target: Rgb, amount: number): Rgb => ({
  r: Math.round(color.r + (target.r - color.r) * amount),
  g: Math.round(color.g + (target.g - color.g) * amount),
  b: Math.round(color.b + (target.b - color.b) * amount),
});

const buildPalette = (baseHex: string): ColorScale => {
  const base = hexToRgb(baseHex);
  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };
  return {
    50: rgbToHex(mix(base, white, 0.9)),
    100: rgbToHex(mix(base, white, 0.8)),
    200: rgbToHex(mix(base, white, 0.65)),
    300: rgbToHex(mix(base, white, 0.5)),
    400: rgbToHex(mix(base, white, 0.25)),
    500: rgbToHex(base),
    600: rgbToHex(mix(base, black, 0.12)),
    700: rgbToHex(mix(base, black, 0.28)),
    800: rgbToHex(mix(base, black, 0.45)),
    900: rgbToHex(mix(base, black, 0.62)),
    950: rgbToHex(mix(base, black, 0.75)),
  };
};

export const BITCLOUD_PRIMARY_NAMES = [
  'bitcloud-indigo',
  'bitcloud-purple',
  'bitcloud-silver',
  'bitcloud-teal',
] as const;

const bitcloudPrimaries: Record<string, ColorScale> = {
  'bitcloud-indigo': buildPalette('#3F41D0'),
  'bitcloud-purple': buildPalette('#8D14B8'),
  'bitcloud-silver': buildPalette('#CED4DA'),
  'bitcloud-teal': buildPalette('#4ECDC4'),
};

export const withBitcloudPrimaries = <T extends Preset>(preset: T): Preset =>
  definePreset(preset, { primitive: bitcloudPrimaries });
